import React from 'react'
import { RootState } from 'constants/interfaces'
import { useSelector } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'
import { isLoggedInSelector } from 'redux/selectors/auth'
import keys from 'constants/keys'
import { canAccessJobs, canAccessPrompts } from 'components/AdminConsole/Jobs/utils'
import { VM_TASK_FLOW_SELECT_RIDS } from 'constants/baseValues'
import { FeatureFlagKey } from 'constants/featureFlags'
import useFeatureFlag from 'hooks/useFeatureFlag'
import { isAgentAllowedSelector } from 'redux/selectors/config'

interface IProps {
  children: JSX.Element
}

export const RequireAuth = ({ children }: IProps) => {
  const isLoggedIn = useSelector(isLoggedInSelector)
  let location = useLocation()
  if (!isLoggedIn) {
    return <Navigate to={keys.ROUTE_NAMES.LOGIN_OTP} state={{ from: location }} />
  }

  return children
}

export const RequireJobsAccess = ({ children }: IProps) => {
  const { uid } = useSelector((state: RootState) => state.auth)
  if (!uid || !canAccessJobs(uid)) {
    return <Navigate to="/admin/users" replace />
  }

  return children
}

export const RequireCompetitionsAccess = ({ children }: IProps) => {
  const { uid } = useSelector((state: RootState) => state.auth)

  if (!uid || !canAccessJobs(uid)) {
    return <Navigate to="/admin/users" replace />
  }
  return children
}

export const RequirePromptsAccess = ({ children }: IProps) => {
  const { uid } = useSelector((state: RootState) => state.auth)

  if (!uid || !canAccessPrompts(uid)) {
    return <Navigate to="/admin/users" replace />
  }
  return children
}

export const RequireAdminAccess = ({ children }: IProps) => {
  const { permission } = useSelector((state: RootState) => state.config.config)

  const isAdmin = permission?.users === 3

  if (!isAdmin) {
    return <Navigate to={keys.ROUTE_NAMES.NEWS_FEED} />
  }

  return children
}

export const RequireMfa = ({ children }: IProps) => {
  const { candidateToLogin } = useSelector((state: RootState) => state.auth)
  if (!candidateToLogin) {
    return <Navigate to={keys.ROUTE_NAMES.LOGIN_PAGE} />
  }

  return children
}

export const RequireLiveRid = ({ children }: IProps) => {
  const rid = useSelector((state: RootState) => state.config.config?.rid)
  const isConfigReady = useSelector((state: RootState) => state.config.isConfigReady)
  // Wait for config to load — on page refresh rid starts as '' before the API call completes.
  // Without this check the guard would immediately redirect while rid is still empty.
  if (!isConfigReady) return null
  if (!VM_TASK_FLOW_SELECT_RIDS.includes(rid)) {
    return <Navigate to={keys.ROUTE_NAMES.NEWS_FEED} replace />
  }
  return children
}

interface IRequireFeatureAccessProps extends IProps {
  flagKey: FeatureFlagKey
  redirectTo?: string
}

export const RequireFeatureAccess = ({ children, flagKey, redirectTo }: IRequireFeatureAccessProps) => {
  const isEnabled = useFeatureFlag(flagKey)
  const isConfigReady = useSelector((state: RootState) => state.config.isConfigReady)
  if (!isConfigReady) return null
  if (!isEnabled) return <Navigate to={redirectTo ?? keys.ROUTE_NAMES.NEWS_FEED} replace />
  return children
}

// Wraps the agent layout (outside RequireAuth), so logged-out users fall through to RequireAuth's redirect.
export const RequireAgentAccess = ({ children }: IProps) => {
  const isLoggedIn = useSelector(isLoggedInSelector)
  const isAgentAllowed = useSelector(isAgentAllowedSelector)
  const isConfigReady = useSelector((state: RootState) => state.config.isConfigReady)
  if (!isLoggedIn) return children
  if (!isConfigReady) return null
  if (!isAgentAllowed) return <Navigate to={keys.ROUTE_NAMES.NEWS_FEED} replace />
  return children
}

export const IntegrationGuard = ({ children }: IProps) => {
  const retailConfig = useSelector((state: RootState) => state.config.retailConfig)
  if (!retailConfig?.integrations?.length) {
    return <Navigate to={keys.ROUTE_NAMES.NEWS_FEED} />
  }

  return children
}
